import React, { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Plan,
  hasRole,
  normalizeClubPlanTier,
  normalizeSubscriptionTier,
  resolveSubscriptionPlanPrice,
  getCurrencyForRegion,
  getCurrencySymbolForCurrency,
  REGION_CONFIG,
  calculateCombinedBundleSubtotal,
  calculateCombinedBundleTotal,
  calculateCombinedBundleDiscount,
  COMBINED_BUNDLE_DISCOUNT_PERCENT,
  canAccessFeature,
  TierFeatureKey,
  type Region,
  type ClubPlanTier,
} from '@court-sync/shared';
import { CreditCard, ChevronDown, Loader2, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../../store/auth';
import { useCurrentAcademy } from '../../hooks/useDataStore';
import { useSubscription } from '../../hooks/useSubscription';
import { useClubModuleAccess } from '../../hooks/useClubModuleAccess';
import { usePlansQuery } from '../../queries/subscription';
import { subscriptionService } from '../../services/subscription';
import { useClubManagementTourStore } from '../../store/clubManagementTour';
import { useEnterpriseUpgradeTourStore } from '../../store/enterpriseUpgradeTour';
import { useProfessionalUpgradeTourStore } from '../../store/professionalUpgradeTour';
import { userService } from '../../services/user';
import type { SubscriptionResponse } from '../../services/subscription';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui';
import { PlanSelectionCard } from '../../components/subscription/PlanSelectionCard';
import { PlanFeaturesModal } from '../../components/subscription/PlanFeaturesModal';
import { formatCardBrand, getPlanPrice } from './settingsUtils';
import {
  CancelSubscriptionModal,
  ChangePlanModal,
  ConfirmActionModal,
  type CancelSubscriptionScope,
  type ChangePlanProductLine,
} from './SettingsModals';

function formatCombinedSubscriptionHeadline(
  academyPlanName: string | undefined,
  clubPlanName: string | undefined
): string {
  const academy = academyPlanName ?? 'Academy';
  const club = clubPlanName ?? 'Club';
  return `${academy} + ${club}`;
}

export default function SettingsSubscriptionTab() {
  const { region, user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const isAcademyAdminOnly =
    !!user && hasRole(user, 'academy_admin') && !hasRole(user, 'academy_owner');
  const {
    subscription,
    plans: availablePlans,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refresh: refreshSubscription,
    refreshAfterPayment,
    fetchPlans,
    hasUsedTrial,
    hasPaymentMethod,
    currentTier,
  } = useSubscription();
  const { refresh: refreshClubEntitlements } = useClubModuleAccess();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelScope, setCancelScope] = useState<CancelSubscriptionScope>('all');
  const [showClubDowngradeModal, setShowClubDowngradeModal] = useState(false);
  const [pendingClubPlan, setPendingClubPlan] = useState<Plan | null>(null);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedProductLine, setSelectedProductLine] = useState<ChangePlanProductLine>('academy');
  const [featuresPlan, setFeaturesPlan] = useState<Plan | null>(null);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const billingRegion = useMemo((): Region => {
    for (const candidate of [subscription?.region, currentAcademy?.region, region]) {
      if (candidate && candidate in REGION_CONFIG) {
        return candidate as Region;
      }
    }
    return 'US';
  }, [subscription?.region, currentAcademy?.region, region]);

  const { data: clubPlans = [] } = usePlansQuery(billingRegion, 'club');

  const subscriptionType = subscription?.subscriptionType ?? 'academy_owner';
  const isClubOnlyOrg = subscriptionType === 'club_owner';
  const isCombinedOrg = subscriptionType === 'combined';
  const hasAcademyLine = subscriptionType === 'academy_owner' || isCombinedOrg;
  const hasClubLine =
    isClubOnlyOrg ||
    isCombinedOrg ||
    Boolean(subscription?.clubPlanTier) ||
    Boolean(subscription?.clubManagementEnabled);
  const isProfessionalPlus = canAccessFeature(currentTier, TierFeatureKey.BRANDED_SUBDOMAIN);

  const currentClubPlan = useMemo(() => {
    const tier = (subscription?.clubPlanTier ?? 'starter') as ClubPlanTier;
    return clubPlans.find(
      (plan) => (normalizeClubPlanTier(plan.internalId ?? null) ?? 'starter') === tier
    );
  }, [clubPlans, subscription?.clubPlanTier]);

  const sortedClubPlans = useMemo(() => {
    if (!clubPlans.length) return [];
    const tierOrder: ClubPlanTier[] = ['starter', 'professional'];
    return [...clubPlans].sort((a, b) => {
      const tierA = normalizeClubPlanTier(a.internalId ?? null) ?? 'starter';
      const tierB = normalizeClubPlanTier(b.internalId ?? null) ?? 'starter';
      const indexA = tierOrder.indexOf(tierA);
      const indexB = tierOrder.indexOf(tierB);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      return getPlanPrice(a, billingRegion) - getPlanPrice(b, billingRegion);
    });
  }, [clubPlans, billingRegion]);

  const sortedPlans = useMemo(() => {
    if (!availablePlans || availablePlans.length === 0) return [];

    const sortOrder = ['Starter', 'Professional', 'Enterprise'];

    return [...availablePlans].sort((a, b) => {
      const indexA = sortOrder.findIndex(name => a.name.includes(name));
      const indexB = sortOrder.findIndex(name => b.name.includes(name));

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      return getPlanPrice(a, billingRegion) - getPlanPrice(b, billingRegion);
    });
  }, [availablePlans, billingRegion]);

  const currentSubscription: SubscriptionResponse | null = subscription
    ? {
      subscription,
      plan: sortedPlans.find(p => p.id === subscription.planId) || null,
    }
    : null;

  const userRegion = billingRegion;
  const regionCurrency = getCurrencyForRegion(userRegion);
  const currencySymbol = getCurrencySymbolForCurrency(regionCurrency);

  const combinedPricing = useMemo(() => {
    if (!isCombinedOrg || !currentSubscription?.plan || !currentClubPlan) {
      return null;
    }
    const academyAmount = getPlanPrice(currentSubscription.plan, billingRegion);
    const clubAmount = getPlanPrice(currentClubPlan, billingRegion);
    const subtotal = calculateCombinedBundleSubtotal(academyAmount, clubAmount);
    const discount = calculateCombinedBundleDiscount(subtotal);
    const total = calculateCombinedBundleTotal(subtotal);
    return { academyAmount, clubAmount, subtotal, discount, total };
  }, [isCombinedOrg, currentSubscription?.plan, currentClubPlan, billingRegion]);

  useEffect(() => {
    void fetchPlans(billingRegion);
  }, [fetchPlans, billingRegion]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('returned_from_portal') !== 'true') {
      return;
    }

    window.history.replaceState({}, '', window.location.pathname);
    void refreshAfterPayment().catch((err: unknown) => {
      console.error('Error refreshing subscription after portal return:', err);
    });
  }, [refreshAfterPayment]);

  const openPlanChange = (plan: Plan, productLine: ChangePlanProductLine) => {
    setSelectedPlan(plan);
    setSelectedProductLine(productLine);
    setShowChangeModal(true);
  };

  const handlePlanChange = (plan: Plan) => {
    openPlanChange(plan, 'academy');
  };

  const handleClubPlanChange = (plan: Plan) => {
    const targetTier = normalizeClubPlanTier(plan.internalId ?? null) ?? 'starter';
    const currentTier = (subscription?.clubPlanTier ?? 'starter') as ClubPlanTier;

    if (currentTier === 'professional' && targetTier === 'starter') {
      setPendingClubPlan(plan);
      setShowClubDowngradeModal(true);
      return;
    }

    openPlanChange(plan, 'club');
  };

  const confirmClubDowngrade = () => {
    if (pendingClubPlan) {
      openPlanChange(pendingClubPlan, 'club');
      setPendingClubPlan(null);
    }
    setShowClubDowngradeModal(false);
  };

  const confirmPlanChange = async (couponCode?: string) => {
    if (!selectedPlan) return;
    try {
      setActionLoading(true);
      const previousAcademyTier =
        selectedProductLine === 'academy' && currentSubscription
          ? normalizeSubscriptionTier(
              currentSubscription.plan?.internalId ?? currentTier ?? null
            ) ?? 'starter'
          : null;

      if (!currentSubscription) {
        await subscriptionService.createSubscription({
          planId: selectedPlan.id,
          couponCode
        });
        toast.success(`Successfully subscribed to ${selectedPlan.name} plan`);
      } else {
        await subscriptionService.upgradeSubscription(selectedPlan.id, couponCode);
        toast.success(`Successfully changed to ${selectedPlan.name} plan`);
      }

      await refreshSubscription();
      await refreshClubEntitlements();

      if (selectedProductLine === 'academy') {
        const newTier = normalizeSubscriptionTier(selectedPlan.internalId ?? null);
        if (previousAcademyTier === 'starter' && newTier && newTier !== 'starter') {
          useProfessionalUpgradeTourStore.getState().requestTour();
          const professionalUnlockBadgesActivatedAt = new Date().toISOString();
          await userService.updatePreferences({ professionalUnlockBadgesActivatedAt });
          const cur = useAuthStore.getState().user;
          if (cur) {
            useAuthStore.setState({
              user: {
                ...cur,
                preferences: {
                  ...cur.preferences,
                  professionalUnlockBadgesActivatedAt,
                },
              },
            });
          }
        } else if (previousAcademyTier === 'professional' && newTier === 'enterprise') {
          useEnterpriseUpgradeTourStore.getState().requestTour();
          const enterpriseUnlockBadgesActivatedAt = new Date().toISOString();
          await userService.updatePreferences({ enterpriseUnlockBadgesActivatedAt });
          const cur = useAuthStore.getState().user;
          if (cur) {
            useAuthStore.setState({
              user: {
                ...cur,
                preferences: {
                  ...cur.preferences,
                  enterpriseUnlockBadgesActivatedAt,
                },
              },
            });
          }
        }
      }

      setShowChangeModal(false);
      setSelectedPlan(null);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to change subscription plan';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    if (!currentSubscription && selectedPlan) {
      const checkoutUrl = await subscriptionService.createSubscriptionCheckoutSession({
        planId: selectedPlan.id,
        region: billingRegion,
        successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href,
      });
      window.location.href = checkoutUrl;
      return;
    }

    const returnUrl = window.location.origin + '/settings?tab=subscription&returned_from_portal=true';
    const portalUrl = await subscriptionService.createCustomerPortalSession(returnUrl);
    window.location.href = portalUrl;
  };

  const openCancelModal = (scope: CancelSubscriptionScope) => {
    setCancelScope(scope);
    setShowCancelModal(true);
  };

  const handleAddProductLine = async (productLine: 'academy' | 'club', plan: Plan) => {
    try {
      setUpsellLoading(true);
      await subscriptionService.addProductLine({ productLine, planId: plan.id });
      await refreshSubscription();
      await refreshClubEntitlements();

      if (productLine === 'club') {
        useClubManagementTourStore.getState().requestTour();
      } else {
        toast.success('Academy plan added to your subscription');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add plan';
      toast.error(message);
    } finally {
      setUpsellLoading(false);
    }
  };

  const confirmCancelSubscription = async () => {
    try {
      setActionLoading(true);
      if (cancelScope === 'all') {
        await subscriptionService.cancelSubscription();
        toast.success('Subscription will be canceled at the end of the current period');
      } else {
        await subscriptionService.removeProductLine(cancelScope);
        toast.success(
          cancelScope === 'academy'
            ? 'Academy management removed from your subscription'
            : 'Club management removed from your subscription'
        );
      }
      await refreshSubscription();
      await refreshClubEntitlements();
      setShowCancelModal(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update subscription';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const effectiveHasPaymentMethod =
    hasPaymentMethod || Boolean(currentSubscription?.subscription?.paymentMethod?.card);

  const getPlanPriceDisplay = (plan: Plan) => {
    const resolvedPrice = resolveSubscriptionPlanPrice(plan, {
      region: userRegion,
      interval: 'month',
    });
    const priceAmount = resolvedPrice?.amountMajor ?? 0;
    const planCurrencySymbol = resolvedPrice?.symbol ?? currencySymbol;
    return `${planCurrencySymbol}${priceAmount}`;
  };

  const renderPlanGrid = (
    plans: Plan[],
    options: {
      accent: 'academy' | 'club';
      getIsCurrent: (plan: Plan) => boolean;
      getCtaLabel: (plan: Plan, isCurrent: boolean) => string;
      onSelect: (plan: Plan) => void;
      getDisabled?: (plan: Plan, isCurrent: boolean) => boolean;
      getCtaClassName?: (plan: Plan, isCurrent: boolean) => string;
      showTrialBadge?: boolean;
    }
  ) => (
    <div className={`grid grid-cols-1 gap-6 ${plans.length <= 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
      {plans.map((plan) => {
        const isCurrentPlan = options.getIsCurrent(plan);
        const ctaLabel = options.getCtaLabel(plan, isCurrentPlan);
        const disabled = options.getDisabled?.(plan, isCurrentPlan) ?? false;
        return (
          <PlanSelectionCard
            key={plan.id}
            plan={plan}
            priceLabel={getPlanPriceDisplay(plan)}
            isCurrentPlan={isCurrentPlan}
            ctaLabel={ctaLabel}
            onSelect={() => options.onSelect(plan)}
            onViewFeatures={setFeaturesPlan}
            disabled={disabled}
            accent={options.accent}
            showTrialBadge={options.showTrialBadge && !currentSubscription}
            ctaClassName={options.getCtaClassName?.(plan, isCurrentPlan) ?? ''}
          />
        );
      })}
    </div>
  );

  if (subscriptionLoading && !currentSubscription && sortedPlans.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading subscription data...</span>
      </div>
    );
  }

  if (subscriptionError && !currentSubscription) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-600">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load Subscription</h3>
            <p className="text-gray-600 dark:text-gray-400">{subscriptionError}</p>
            <Button variant="outline" className="mt-4" onClick={() => fetchPlans(billingRegion)}>
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {isAcademyAdminOnly && !currentSubscription && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Academy subscription</h3>
            <p className="text-gray-600 dark:text-gray-400">
              CourtSync billing for your academy is managed by the academy owner. This account does not have its own platform subscription.
            </p>
          </CardContent>
        </Card>
      )}

      {currentSubscription ? (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Current Subscription
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your active subscription plan and billing details
            </p>
          </CardHeader>
          <CardContent>
            {currentSubscription.subscription?.isTrialActive && (
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-1">Free Trial Active</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        {currentSubscription.subscription?.daysLeftInTrial !== undefined && currentSubscription.subscription.daysLeftInTrial > 0
                          ? `Your free trial ends in ${currentSubscription.subscription.daysLeftInTrial} ${currentSubscription.subscription.daysLeftInTrial === 1 ? 'day' : 'days'}`
                          : 'Your free trial ends today'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-slate-200 p-6 dark:border-slate-700">
              {isCombinedOrg ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {formatCombinedSubscriptionHeadline(
                      currentSubscription.plan?.name,
                      currentClubPlan?.name ??
                        (subscription?.clubPlanTier === 'professional' ? 'Club Pro' : 'Club')
                    )}
                  </h3>
                  {combinedPricing ? (
                    <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <p>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {currencySymbol}{combinedPricing.total.toFixed(2)}/month
                        </span>
                        {' '}combined
                        {' '}
                        <span className="text-emerald-700 dark:text-emerald-400">
                          ({COMBINED_BUNDLE_DISCOUNT_PERCENT}% bundle discount applied)
                        </span>
                      </p>
                      <p className="text-xs">
                        {currentSubscription.plan?.name}: {currencySymbol}{combinedPricing.academyAmount}/mo
                        {' · '}
                        {currentClubPlan?.name ?? 'Club'}: {currencySymbol}{combinedPricing.clubAmount}/mo
                        {' · '}
                        Discount: −{currencySymbol}{combinedPricing.discount.toFixed(2)}
                      </p>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {hasClubLine && isClubOnlyOrg ? 'Club plan' : 'Academy plan'}
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                    {isClubOnlyOrg
                      ? currentClubPlan?.name ??
                        (subscription?.clubPlanTier === 'professional' ? 'Club Pro' : 'Club')
                      : currentSubscription.plan?.name || 'Academy plan'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {isClubOnlyOrg
                      ? currentClubPlan?.description ||
                        'Court hire, memberships, match play, and facility operations'
                      : currentSubscription.plan?.description || 'Lesson and academy management'}
                  </p>
                </>
              )}

              {isCombinedOrg ? (
                <div className="mt-4 space-y-2 border-t border-slate-200 pt-4 dark:border-slate-700">
                  {hasAcademyLine && currentSubscription.plan ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-blue-50/80 px-3 py-2 dark:bg-blue-950/30">
                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Academy · {currentSubscription.plan.name}
                      </span>
                      <span className="text-sm text-blue-800 dark:text-blue-200">
                        {getPlanPriceDisplay(currentSubscription.plan)}/month
                      </span>
                    </div>
                  ) : null}
                  {hasClubLine ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-violet-50/80 px-3 py-2 dark:bg-violet-950/30">
                      <span className="text-sm font-medium text-violet-900 dark:text-violet-100">
                        Club ·{' '}
                        {currentClubPlan?.name ??
                          (subscription?.clubPlanTier === 'professional' ? 'Club Pro' : 'Club')}
                      </span>
                      {currentClubPlan ? (
                        <span className="text-sm text-violet-800 dark:text-violet-200">
                          {getPlanPriceDisplay(currentClubPlan)}/month
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 p-4 dark:border-slate-700">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Billing status</p>
                <p className="font-medium text-slate-900 dark:text-white">
                  {currentSubscription.subscription?.status === 'trialing'
                    ? 'Free trial'
                    : currentSubscription.subscription?.status === 'active'
                      ? currentSubscription.subscription?.cancelAtPeriodEnd
                        ? 'Cancelled'
                        : 'Active'
                      : 'Past due'}
                </p>
              </div>
              <div className="text-right text-sm text-slate-600 dark:text-slate-400">
                {currentSubscription.subscription?.cancelAtPeriodEnd
                  ? `Ends ${new Date(currentSubscription.subscription?.currentPeriodEnd ?? Date.now()).toLocaleDateString()}`
                  : `Renews ${new Date(currentSubscription.subscription?.currentPeriodEnd ?? Date.now()).toLocaleDateString()}`}
              </div>
            </div>

            {currentSubscription.subscription?.cancelAtPeriodEnd && (
              <div className="mt-4 p-4 bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800 rounded-lg flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-900 dark:text-orange-100">Subscription Cancelled</p>
                  <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                    Your subscription will end on {new Date(currentSubscription.subscription?.currentPeriodEnd ?? Date.now()).toLocaleDateString()}.
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 border border-gray-200 rounded-lg p-6 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Billing Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {currentSubscription.subscription?.isTrialActive ? 'Trial End Date' : 'Next Billing Date'}
                  </p>
                  <p className="font-medium">
                    {currentSubscription.subscription?.isTrialActive && currentSubscription.subscription?.trialEnd
                      ? new Date(currentSubscription.subscription.trialEnd).toLocaleDateString()
                      : new Date(currentSubscription.subscription?.currentPeriodEnd ?? Date.now()).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Payment Method</p>
                  {currentSubscription.subscription?.paymentMethod?.card ? (
                    <div>
                      <p className="font-medium">
                        {formatCardBrand(currentSubscription.subscription.paymentMethod.card.brand)} •••• {currentSubscription.subscription.paymentMethod.card.last4}
                      </p>
                      <button
                        type="button"
                        onClick={() => void handleManageBilling()}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-2 underline"
                      >
                        Update payment method
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleManageBilling()}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-2 underline"
                    >
                      Add or update payment method
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-6">
              <Button variant="default" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800" onClick={() => void handleManageBilling()}>
                Manage Billing & Payment Methods
              </Button>
              {isCombinedOrg ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="text-red-600 hover:text-red-700">
                      Cancel
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => openCancelModal('all')}>
                      Cancel entire subscription
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openCancelModal('academy')}>
                      Remove academy management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openCancelModal('club')}>
                      Remove club management
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => openCancelModal('all')}>
                  Cancel Subscription
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : !isAcademyAdminOnly ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Active Subscription</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You don&apos;t have an active subscription yet. Choose a plan below to unlock all features.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {hasAcademyLine || !currentSubscription ? (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {currentSubscription
                ? isCombinedOrg
                  ? 'Manage academy plan'
                  : 'Available Plans'
                : 'Available Plans'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {currentSubscription && isCombinedOrg
                ? 'Change your academy tier. Changes take effect immediately with proration.'
                : "Choose the plan that best fits your academy's needs"}
            </p>
          </CardHeader>
          <CardContent>
            {subscriptionLoading && sortedPlans.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : sortedPlans.length === 0 ? (
              <p className="text-center text-gray-600 dark:text-gray-400 py-8">No academy plans available.</p>
            ) : (
              renderPlanGrid(sortedPlans, {
                accent: 'academy',
                showTrialBadge: !hasUsedTrial,
                getIsCurrent: (plan) => currentSubscription?.plan?.id === plan.id,
                getCtaLabel: (plan, isCurrent) => {
                  if (isCurrent && currentSubscription && !currentSubscription.subscription?.cancelAtPeriodEnd) {
                    return 'Current Plan';
                  }
                  if (isCurrent && currentSubscription?.subscription?.cancelAtPeriodEnd) {
                    return `Renew ${plan.name}`;
                  }
                  if (!currentSubscription) {
                    return `Subscribe to ${plan.name}`;
                  }
                  return `Switch to ${plan.name}`;
                },
                onSelect: handlePlanChange,
                getDisabled: (plan, isCurrent) =>
                  Boolean(isCurrent && currentSubscription && !currentSubscription.subscription?.cancelAtPeriodEnd),
                getCtaClassName: (plan, isCurrent) =>
                  !isCurrent && plan.name.toLowerCase().includes('enterprise')
                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
                    : '',
              })
            )}
          </CardContent>
        </Card>
      ) : null}

      {currentSubscription && isClubOnlyOrg && sortedPlans.length > 0 ? (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add academy management</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Expand your subscription with lesson scheduling, student portals, and coaching tools.
            </p>
          </CardHeader>
          <CardContent>
            {renderPlanGrid(sortedPlans, {
              accent: 'academy',
              getIsCurrent: () => false,
              getCtaLabel: () => 'Add academy plan',
              onSelect: (plan) => void handleAddProductLine('academy', plan),
              getDisabled: () => upsellLoading,
            })}
          </CardContent>
        </Card>
      ) : null}

      {currentSubscription && hasClubLine && sortedClubPlans.length > 0 ? (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {isCombinedOrg ? 'Manage club plan' : 'Club plans'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Change your club product line tier. Downgrading from Club Pro disables branded subdomains.
            </p>
          </CardHeader>
          <CardContent>
            {renderPlanGrid(sortedClubPlans, {
              accent: 'club',
              getIsCurrent: (plan) => {
                const planTier = normalizeClubPlanTier(plan.internalId ?? null) ?? 'starter';
                return (subscription?.clubPlanTier ?? 'starter') === planTier;
              },
              getCtaLabel: (plan, isCurrent) =>
                isCurrent ? 'Current club plan' : `Switch to ${plan.name}`,
              onSelect: handleClubPlanChange,
              getDisabled: (_plan, isCurrent) => isCurrent || actionLoading,
              getCtaClassName: (plan, isCurrent) =>
                !isCurrent && plan.name.toLowerCase().includes('pro')
                  ? 'bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800'
                  : '',
            })}
          </CardContent>
        </Card>
      ) : null}

      {currentSubscription &&
      subscriptionType === 'academy_owner' &&
      !hasClubLine &&
      isProfessionalPlus &&
      sortedClubPlans.length > 0 ? (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add club management</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Add a dedicated club plan for court hire, memberships, and facility operations.
            </p>
          </CardHeader>
          <CardContent>
            {renderPlanGrid(sortedClubPlans, {
              accent: 'club',
              getIsCurrent: () => false,
              getCtaLabel: (plan) => `Add ${plan.name}`,
              onSelect: (plan) => void handleAddProductLine('club', plan),
              getDisabled: () => upsellLoading,
              getCtaClassName: (plan) =>
                plan.name.toLowerCase().includes('pro')
                  ? 'bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800'
                  : '',
            })}
          </CardContent>
        </Card>
      ) : null}

      <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 dark:text-blue-100">Plan Changes</h4>
        <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
          Plan changes take effect immediately. Downgrades will be prorated, and upgrades will be charged the difference.
          Removing a product line from a combined subscription also takes effect immediately.
        </p>
      </div>

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        scope={cancelScope}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancelSubscription}
        loading={actionLoading}
      />
      <ConfirmActionModal
        isOpen={showClubDowngradeModal}
        title="Downgrade to Club"
        intro="Downgrading to Club will disable your branded club subdomain. Visitors will need to use /club/:slug path URLs instead."
        confirmLabel="Continue downgrade"
        onClose={() => {
          setShowClubDowngradeModal(false);
          setPendingClubPlan(null);
        }}
        onConfirm={confirmClubDowngrade}
      />
      <ChangePlanModal
        isOpen={showChangeModal}
        selectedPlan={selectedPlan}
        currentSubscription={currentSubscription}
        currentClubPlan={currentClubPlan ?? null}
        productLine={selectedProductLine}
        isCombinedOrg={isCombinedOrg}
        onClose={() => { setShowChangeModal(false); setSelectedPlan(null); }}
        onConfirm={confirmPlanChange}
        loading={actionLoading}
        currencyCode={regionCurrency}
        currencySymbol={currencySymbol}
        hasPaymentMethod={effectiveHasPaymentMethod}
        onManageBilling={handleManageBilling}
      />
      <PlanFeaturesModal
        plan={featuresPlan}
        open={featuresPlan != null}
        onOpenChange={(open) => {
          if (!open) setFeaturesPlan(null);
        }}
      />
    </div>
  );
}
